"use client";

import { ProgressBarLink, useProgressNavigation } from "../progress-bar";
import AIBadge from "../ui/ai-badge";
import { useEffect, useMemo, useState } from "react";
import { BsPauseFill, BsPlayFill, BsX } from "react-icons/bs";
import { FaGripVertical } from "react-icons/fa6";
import { FiAlertCircle, FiVideo } from "react-icons/fi";
import { LuMic } from "react-icons/lu";
import { MicPermissionWarning } from "@/app/[tenant]/(console)/scribe/playground/[id]/mic-permission-warning";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui";
import Button from "@/components/ui/button";
import { queryClient } from "@/config/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { useMicrophoneController } from "@/hooks/use-audio-devices";
import { useBeforeUnload } from "@/hooks/use-before-unload";
import { useDraggable } from "@/hooks/use-draggable";
import { isLocalNoteId, useRecordingNote, useSelectedNote } from "@/hooks/use-selected-note";
import { useTelehealthBotLiveUpdates } from "@/hooks/use-telehealth-bot-live-updates";
import { useTelehealthHydration } from "@/hooks/use-telehealth-hydration";
import { useTelehealthRecordingSync } from "@/hooks/use-telehealth-recording-sync";
import { useWakeLock } from "@/hooks/use-wake-lock";
import { getSupportedMimeType } from "@/lib/audio";
import { formatName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { CreateNoteState } from "@/types/enums/note-state";
import AiScribeConsentDialog from "./ai-scribe-consent-dialog";
import MiniAudioVisualizer from "./mini-audio-visualizer";
import { Timer } from "./timer";
import { saveAiScribeConsent } from "@/api/provider/scribe/notes";
import { updatePatientInCache } from "@/api/provider/user/patient/patient-cache-utils";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { type HandlePlayPausePatient, useScribeNote, useScribeRecording, useScribeSession, useScribeStore } from "@/stores/scribe-store";
export default function RecordingPiP() {
  const {
    navigateWithProgress,
    runWithNavigationGuard
  } = useProgressNavigation();
  const mimeType = getSupportedMimeType();

  // Track device select open state to disable drag while menu is open
  const [isDeviceSelectOpen, setIsDeviceSelectOpen] = useState(false);

  // Track sonner toaster height so PIP can avoid overlapping toasts
  const [toasterBottom, setToasterBottom] = useState(0);
  useEffect(() => {
    const toaster = document.querySelector("[data-sonner-toaster]");
    if (!toaster) return;
    const update = () => {
      const toasts = toaster.querySelectorAll("[data-sonner-toast]");
      if (toasts.length === 0) {
        setToasterBottom(0);
        return;
      }
      const maxBottom = Math.max(...Array.from(toasts, t => t.getBoundingClientRect().bottom));
      setToasterBottom(maxBottom + 8);
    };
    const observer = new MutationObserver(update);
    observer.observe(toaster, {
      childList: true,
      subtree: true,
      attributes: true
    });
    update();
    return () => observer.disconnect();
  }, []);
  const {
    containerRef,
    position,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel
  } = useDraggable<HTMLDivElement>({
    viewportPadding: 8,
    disabled: isDeviceSelectOpen
  });
  const {
    visiblePatientId,
    audioStream,
    recordingNoteState,
    handlePlayPause,
    currentRecordingId,
    onScribePage,
    isActive,
    isStartingRecording,
    setRecordingId,
    setIsTranscribing,
    flushTranscription
  } = useScribeRecording();
  const {
    selectedNote
  } = useSelectedNote();
  const {
    recordingNote
  } = useRecordingNote();
  const {
    requestGenerateNote
  } = useScribeSession();
  const {
    recordingNoteTemplateId
  } = useScribeNote();

  // Adapter is mounted here (one global instance) so server lifecycle drives
  // the recording slice even when the provider has navigated away from the
  // playground. Reads the active recording note's event id.
  const {
    tailDrained,
    botStatus,
    transcriptReady,
    isTelehealthNote
  } = useTelehealthRecordingSync(recordingNote);
  // Live-patches the bot embedded in the `NoteDetail` cache from the
  // `TELEHEALTH_BOT_STATUS` WS subscription. Mounted once globally here so the
  // patches still fire when the provider has navigated away from the
  // playground.
  useTelehealthBotLiveUpdates(recordingNote);
  // Switches the PIP to a newly-admitted telehealth Note on a fresh
  // visit/rejoin. Mounted globally here (not in the appointment list) so the
  // bot-admitted alert still lands after the Join click navigates away.
  useTelehealthHydration();

  // Element-level UX divergence only — state-machine transitions go through
  // the recording slice. Keyed off the bot (not the in-memory `audioSource`)
  // so the meeting variant survives a page refresh and a telehealth note can
  // never fall back to mic-recording chrome before `audioSource` re-resolves.
  const isTelehealth = isTelehealthNote;
  // Bot has left the meeting — recording can't be resumed. PIP stays for nav
  // but the play button is greyed.
  const isTelehealthEnded = isTelehealth && tailDrained;
  // Bot left the meeting but the authoritative transcript hasn't been
  // reconciled yet — show an intermediate "finalizing" affordance.
  const isTelehealthFinalizing = isTelehealth && botStatus === "ended" && !transcriptReady;

  // Mic chrome — for telehealth the device list is empty (auto-hiding the
  // selector) and the browser mic-permission concept doesn't apply, so we
  // treat permission as granted to avoid the warning UI.
  const micController = useMicrophoneController();
  const {
    selectedDevice,
    selectDevice,
    audioDevices
  } = micController;
  const micPermissionGranted = isTelehealth ? true : micController.micPermissionGranted;
  const {
    request: _requestWakeLock,
    release: _releaseWakeLock
  } = useWakeLock();
  const onCurrentNote = useMemo(() => onScribePage && selectedNote?.id === currentRecordingId, [onScribePage, selectedNote?.id, currentRecordingId]);
  const needsToStartRecording = useMemo(() => !isActive && !recordingNote?.transcript?.id && recordingNote?.aiGenerated, [isActive, recordingNote]);
  const {
    tenant
  } = useTenant();
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const recordingPatient = recordingNote?.patient;
  // Consent is re-read whenever mic audio is about to start, resumes
  // included: a note that already exists never goes back through
  // /start-recording. A No always blocks; with the clinic's check on, anything
  // short of a Yes asks. A patient who agreed is only re-read, not asked again.
  const askForConsent = !!tenant?.aiScribeConsentCheckEnabled;
  const rereadsConsent = !isTelehealth && recordingNoteState !== CreateNoteState.RECORDING;
  const isOnCurrentPatient = useMemo(() => visiblePatientId === recordingNote?.patient?.id, [visiblePatientId, recordingNote]);

  // Show PIP when there's an active recording. The adapter clears
  // currentRecordingId once the telehealth bridge finalises, so the same
  // rule covers both mic and telehealth — when there's nothing to track,
  // the PIP hides.
  const isVisible = !!currentRecordingId && (onScribePage ? true : needsToStartRecording || recordingNoteState !== CreateNoteState.IDLE);
  const isActivelyRecording = recordingNoteState === CreateNoteState.RECORDING;
  useBeforeUnload(isActivelyRecording, "You are currently recording a note. Are you sure you want to leave? Your recording will be lost.");
  const requestWakeLock = async () => {
    try {
      await _requestWakeLock();
    } catch (error) {
      console.warn("Failed to acquire wake lock:", error);
    }
  };
  const releaseWakeLock = async () => {
    try {
      await _releaseWakeLock();
    } catch (error_0) {
      console.warn("Failed to release wake lock:", error_0);
    }
  };
  const handleSelectDevice = (deviceId: string) => {
    void selectDevice(deviceId, {
      mimeType,
      chunkMs: 30000
    });
  };

  // Avoid auto-requesting mic on mount; only request when starting recording

  // Carry the service's note template (set when the note was launched from an
  // appointment checklist) so the playground generates against it instead of
  // re-prompting for a template.
  const noteHref = currentRecordingId ? `/scribe/playground/${currentRecordingId}${recordingNoteTemplateId ? `?noteTemplateId=${recordingNoteTemplateId}` : ""}` : "";
  const handleGenerateNote = () => {
    if (!currentRecordingId) return;
    runWithNavigationGuard(noteHref, () => {
      requestGenerateNote();
      navigateWithProgress(noteHref, true, {
        bypassUnsavedChangesGuard: true
      });
    });
  };
  const handleStopRecording = async () => {
    // Mic-only. Telehealth has no in-PIP stop — the visit ends when the
    // provider closes Meet and Recall's auto-terminate fires the bot.fatal
    // / bot.done webhook, which closes the bridge and unsticks the slice.
    await releaseWakeLock();
    setIsTranscribing(true);
    try {
      await flushTranscription();
    } finally {
      setIsTranscribing(false);
    }
    setRecordingId(undefined);
  };
  const handlePlayPauseClick = async (consentConfirmed?: boolean, patient: HandlePlayPausePatient | undefined = recordingPatient) => {
    if (recordingNoteState === CreateNoteState.RECORDING) {
      await releaseWakeLock();
    } else {
      await requestWakeLock();
    }
    return handlePlayPause(patient, consentConfirmed);
  };
  const handleStartClick = async () => {
    if (rereadsConsent && recordingPatient) {
      let record;
      try {
        record = await queryClient.fetchQuery({
          ...patientQueryOptions.detail(recordingPatient.id),
          staleTime: 0
        });
      } catch {
        // An unchecked answer never authorizes recording.
        toast({
          title: "Error",
          description: "Couldn't check the patient's AI scribe consent. Try again.",
          variant: "error"
        });
        return;
      }
      const consent = record?.aiScribeConsent;
      if (askForConsent && consent !== true) {
        setIsConsentDialogOpen(true);
        return;
      }
      if (consent === false) {
        toast({
          title: "Patient opted out of AI scribe",
          description: "Recording wasn't started. Use a manual note instead."
        });
        return;
      }
      // The note's embedded patient may still hold an older answer.
      await handlePlayPauseClick(undefined, {
        ...recordingPatient,
        aiScribeConsent: consent
      });
      return;
    }
    await handlePlayPauseClick();
  };
  const handleConsentAgreed = async () => {
    setIsConsentDialogOpen(false);
    if (!recordingPatient) return;
    const patientId = recordingPatient.id;
    const previous = recordingPatient.aiScribeConsent ?? null;
    updatePatientInCache(patientId, {
      aiScribeConsent: true
    });
    // A new note gets the Yes from /start-recording, linked to that note. A
    // note that already exists skips that call, so the Yes is sent alongside;
    // if it isn't saved (an empty response counts), the recording is stopped.
    const existingNoteId = currentRecordingId && !isLocalNoteId(currentRecordingId) ? currentRecordingId : undefined;
    if (existingNoteId) {
      saveAiScribeConsent(patientId, true, existingNoteId).then(saved => {
        if (!saved) throw new Error("No response");
      }).catch(() => {
        updatePatientInCache(patientId, {
          aiScribeConsent: previous
        });
        useScribeStore.getState().pauseAudio();
        toast({
          title: "Recording stopped",
          description: "Couldn't save the patient's AI scribe consent. Try again.",
          variant: "error"
        });
      });
    }
    const started = await handlePlayPauseClick(true);
    if (!started && !existingNoteId) {
      updatePatientInCache(patientId, {
        aiScribeConsent: previous
      });
    }
  };
  const handleConsentDeclined = () => {
    if (!recordingPatient) return;
    const patientId_0 = recordingPatient.id;
    const previous_0 = recordingPatient.aiScribeConsent ?? null;
    updatePatientInCache(patientId_0, {
      aiScribeConsent: false
    });
    setIsConsentDialogOpen(false);
    setRecordingId(undefined);
    toast({
      title: "Recording not started",
      description: "The patient declined AI scribe. Use a manual note for this visit."
    });
    saveAiScribeConsent(patientId_0, false).then(saved_0 => {
      // An empty response (e.g. an aborted request) doesn't prove it was saved.
      if (!saved_0) throw new Error("No response");
    }).catch(() => {
      updatePatientInCache(patientId_0, {
        aiScribeConsent: previous_0
      });
      toast({
        title: "Error",
        description: "Couldn't save that the patient declined AI scribe.",
        variant: "error"
      });
    });
  };
  if (!isVisible) return null;
  return <div ref={containerRef} className={cx("fixed z-40 flex max-w-48 cursor-grab flex-wrap items-center gap-2 rounded-lg border bg-background/70 px-3 py-2 shadow-lg backdrop-blur-md", "md:max-w-none", "motion-preset-fade-md motion-scale-in-75", !micPermissionGranted && "border-status-error-text")} style={{
    left: position ? `${position.x}px` : undefined,
    top: position ? `${position.y}px` : toasterBottom ? `${toasterBottom}px` : undefined,
    right: position?.x ? undefined : "50px",
    transition: position ? undefined : "top 200ms ease"
  }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>
      {!isTelehealth && !onCurrentNote && <Button variant="ghost" className="-m-2 px-2" onClick={() => {
      void handleStopRecording();
    }}>
          <BsX className="stroke-[currentColor] text-lg" />
          <span className="sr-only">Stop Recording</span>
        </Button>}
      {!isTelehealth && !onCurrentNote && !isOnCurrentPatient && <div className="text-caption text-muted-foreground">
          {formatName(recordingNote?.patient?.firstName, recordingNote?.patient?.lastName)}
        </div>}
      {!isTelehealth && audioDevices.length > 0 && micPermissionGranted && <div className="relative z-[60] flex items-center" data-no-drag>
          <Select value={selectedDevice} onValueChange={handleSelectDevice} onOpenChange={setIsDeviceSelectOpen}>
            <SelectTrigger className="w-24 text-caption md:w-48">
              <div className="flex items-center">
                <LuMic className="mr-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Select microphone" className="flex-1 overflow-hidden" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {audioDevices.map(device => <SelectItem key={device.deviceId} value={device.deviceId} className="text-caption">
                  {device.label}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>}
      {isTelehealth && <div className="flex items-center gap-1.5 rounded-md bg-background-secondary px-2 py-1 text-caption text-muted-foreground">
          <FiVideo className="h-3.5 w-3.5 shrink-0" />
          <span>
            {isTelehealthFinalizing ? "Visit ended — finalizing transcript…" : isTelehealthEnded ? "Visit ended" : "Recording from meeting"}
          </span>
        </div>}
      <div className="flex items-center gap-2 rounded-xl bg-background-secondary px-2 py-1.5">
        {recordingNoteState === CreateNoteState.RECORDING ? <div className="relative">
            <div className="absolute -left-0.5 -top-0.5 h-2.5 w-2.5 animate-ping rounded-full bg-status-error-text opacity-75" />
            <div className="relative h-1.5 w-1.5 rounded-full bg-status-error-text" />
          </div> : <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />}
        {!isTelehealth && <span className={cx("text-body font-medium", !micPermissionGranted && "opacity-50")}>
            <Timer />
          </span>}
      </div>
      {/* Audio waveform is mic-only — Recall streams audio server-side so
          there's no client MediaStream to drive the visualizer. The pulsing
          dot above is the shared "is recording" indicator. */}
      {!isTelehealth && <div className="hidden rounded-xl bg-background-secondary p-1 lg:block">
          <MiniAudioVisualizer stream={audioStream} isActive={recordingNoteState === CreateNoteState.RECORDING} className="block" mode="dual" lowCutoffHz={200} highCutoffHz={5000} />
        </div>}
      {(recordingNoteState === CreateNoteState.RECORDING || recordingNoteState === CreateNoteState.PAUSED || needsToStartRecording ||
    // Mic note resumed from a reset store (refresh / direct URL): state is
    // back to IDLE but a transcript already exists, so we still need the
    // play button to restart MediaRecorder on the same note. Telehealth is
    // server-driven and never resumes this way.
    !isTelehealth && !!recordingNote?.transcript?.id) && <Button variant="secondary" className="px-2" onClick={() => void handleStartClick()} disabled={!micPermissionGranted || isTelehealthEnded} tooltip={isTelehealthEnded ? "The bot has left the meeting. Generate the note to finish." : undefined} isLoading={isStartingRecording} loadingText={needsToStartRecording ? "Starting Recording..." : "Resuming Recording..."}>
          {recordingNoteState === CreateNoteState.RECORDING ? <>
              <BsPauseFill />
            </> : needsToStartRecording ? <>
              <BsPlayFill className="mr-1" />
              Start
              <span className="hidden md:inline">&nbsp;Recording Note</span>
            </> : <>
              <BsPlayFill />
            </>}

          <span className="sr-only">
            {recordingNoteState === CreateNoteState.RECORDING ? "Pause" : "Play"}
          </span>
        </Button>}
      {!onCurrentNote && !needsToStartRecording && currentRecordingId && !isLocalNoteId(currentRecordingId) && <ProgressBarLink href={noteHref}>
            <Button variant="secondary">
              <span className="hidden md:inline">Go to note</span>
              <span className="inline md:hidden">View</span>
            </Button>
          </ProgressBarLink>}
      {(!onCurrentNote || !onScribePage) && !needsToStartRecording && <Button className="group/button text-nowrap" variant="ai" onClick={handleGenerateNote} isLoading={isStartingRecording} loadingText="Starting Recording..." icon={<AIBadge className="group-hover/button:motion-rotate-in-[360deg]" />}>
          Generate<span className="hidden md:inline">&nbsp;Note</span>
        </Button>}
      <FaGripVertical className="opacity-50" />
      <AiScribeConsentDialog open={isConsentDialogOpen} onOpenChange={setIsConsentDialogOpen} patientName={formatName(recordingPatient?.firstName, recordingPatient?.lastName)} onAgreed={() => void handleConsentAgreed()} onDeclined={handleConsentDeclined} />
      {!micPermissionGranted && !onScribePage && <Dialog>
          <DialogTrigger>
            <div className="absolute inset-0 flex cursor-pointer items-center gap-2 rounded-md bg-status-error-bg/70 p-1 backdrop-blur-md transition-colors hover:bg-status-error-bg/90">
              <FiAlertCircle className="ml-4 h-4 w-4 text-status-error-text" />
              <div className="text-left *:text-status-error-text">
                <p className="font-medium">Microphone permission required</p>
                <p className="text-caption">
                  Please grant microphone access to use this feature
                </p>
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className="p-0">
            <DialogHeader className="sr-only">
              <DialogTitle className="sr-only">
                Microphone permission required
              </DialogTitle>
              <DialogDescription className="sr-only">
                Please grant microphone access to use this feature
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="p-0">
              <MicPermissionWarning className="m-0" />
            </DialogBody>
          </DialogContent>
        </Dialog>}
    </div>;
}